import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Divider,
    FormControlLabel,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import IconButton from "@mui/material/IconButton";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Loader } from "./styles";
import { toast } from "react-toastify";
import { useState } from "react";

import {
    LINHA_INICIAL_DADOS,
    ResultadoConversao,
    baixarCsv,
    converterArquivoCemig,
    formatarCota,
    gerarCsv,
} from "@/utils/cemigCsv";

const LINHAS_PREVIA = 20;

interface IProps {
    onClose: () => void;
}

interface IFalha {
    nome: string;
    mensagem: string;
}

export default function ConversorCemig({ onClose }: IProps) {
    const [resultados, setResultados] = useState<ResultadoConversao[]>([]);
    const [falhas, setFalhas] = useState<IFalha[]>([]);
    const [processando, setProcessando] = useState(false);
    const [incluirCabecalho, setIncluirCabecalho] = useState(false);
    const [arrastando, setArrastando] = useState(false);

    const processarArquivos = async (arquivos: File[]) => {
        const planilhas = arquivos.filter((arquivo) => arquivo.name.split(".").pop()?.toLowerCase() === "xlsx");

        if (planilhas.length !== arquivos.length) {
            toast.error("Selecione apenas arquivos .xlsx gerados pelo Rebalance (layout FCB da CEMIG).");
        }

        if (planilhas.length === 0) {
            return;
        }

        setProcessando(true);

        const convertidos: ResultadoConversao[] = [];
        const naoConvertidos: IFalha[] = [];

        for (let i = 0; i < planilhas.length; i++) {
            try {
                convertidos.push(await converterArquivoCemig(planilhas[i]));
            } catch (erro: any) {
                naoConvertidos.push({
                    nome: planilhas[i].name,
                    mensagem: erro?.message ?? "Não foi possível ler o arquivo.",
                });
            }
        }

        setResultados(convertidos);
        setFalhas(naoConvertidos);
        setProcessando(false);
    };

    const handleFileInput = (evento: any) => {
        const lista: File[] = Array.prototype.slice.call(evento.target.files || []);
        evento.target.value = "";
        processarArquivos(lista);
    };

    const handleDrop = (evento: any) => {
        evento.preventDefault();
        setArrastando(false);
        processarArquivos(Array.prototype.slice.call(evento.dataTransfer?.files || []));
    };

    const baixar = (resultado: ResultadoConversao) => {
        baixarCsv(resultado.nomeCsv, gerarCsv(resultado, incluirCabecalho));
    };

    const baixarTodos = async () => {
        const validos = resultados.filter((resultado) => resultado.erros.length === 0);

        for (let i = 0; i < validos.length; i++) {
            baixar(validos[i]);
            // O navegador bloqueia downloads disparados em sequência imediata.
            await new Promise((resolve) => window.setTimeout(resolve, 400));
        }
    };

    const copiar = async (resultado: ResultadoConversao) => {
        const conteudo = gerarCsv(resultado, incluirCabecalho);

        try {
            await navigator.clipboard.writeText(conteudo);
            toast.success("CSV copiado para a área de transferência.");
        } catch (erro) {
            const area = document.createElement("textarea");

            area.value = conteudo;
            document.body.appendChild(area);
            area.select();
            document.execCommand("copy");
            document.body.removeChild(area);

            toast.success("CSV copiado para a área de transferência.");
        }
    };

    const totalValidos = resultados.filter((resultado) => resultado.erros.length === 0).length;

    return (
        <Dialog
            maxWidth="lg"
            fullWidth
            onClose={(_, reason) => {
                if (reason !== "backdropClick") {
                    onClose();
                }
            }}
            open
        >
            <DialogTitle>
                Conversor CEMIG — XLSX do Rebalance para o novo CSV
                <IconButton
                    aria-label="close"
                    onClick={() => onClose()}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box
                            onDragOver={(evento) => {
                                evento.preventDefault();
                                setArrastando(true);
                            }}
                            onDragLeave={() => setArrastando(false)}
                            onDrop={handleDrop}
                            sx={{
                                border: "2px dashed",
                                borderColor: arrastando ? "primary.main" : "divider",
                                backgroundColor: arrastando ? "action.hover" : "transparent",
                                borderRadius: 1,
                                p: 3,
                                textAlign: "center",
                            }}
                        >
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Arraste aqui os arquivos <strong>FCB (.xlsx)</strong> gerados pelo Rebalance — um por
                                usina — ou selecione abaixo.
                            </Typography>

                            <Button variant="contained" component="label" startIcon={<UploadFileIcon />}>
                                Selecionar arquivos
                                <input type="file" accept=".xlsx" hidden multiple onChange={handleFileInput} />
                            </Button>

                            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                Os dados são lidos a partir da linha {LINHA_INICIAL_DADOS} (colunas A, B e C).
                            </Typography>
                        </Box>
                    </Grid>

                    {processando && (
                        <Grid item xs={12}>
                            <Loader>
                                <img alt="Loading" src="/Loading.gif" width="40px" height="40px" />
                            </Loader>
                        </Grid>
                    )}

                    {falhas.map((falha) => (
                        <Grid item xs={12} key={falha.nome}>
                            <Alert severity="error">
                                <strong>{falha.nome}</strong> — {falha.mensagem}
                            </Alert>
                        </Grid>
                    ))}

                    {resultados.map((resultado) => {
                        const bloqueado = resultado.erros.length > 0;
                        const previa = resultado.linhas.slice(0, LINHAS_PREVIA);

                        return (
                            <Grid item xs={12} key={resultado.nomeArquivo}>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            flexWrap: "wrap",
                                            gap: 1,
                                            mb: 1,
                                        }}
                                    >
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                            {resultado.nomeArquivo}
                                        </Typography>

                                        <Chip
                                            size="small"
                                            color={bloqueado ? "error" : "success"}
                                            label={bloqueado ? "Com erro" : "Pronto"}
                                        />
                                    </Box>

                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        {resultado.linhas.length} linha(s) · soma das cotas{" "}
                                        {formatarCota(resultado.somaCotas)}%
                                        {resultado.ancora
                                            ? ` · âncora: UC ${resultado.ancora.uc} (${resultado.ancora.cotaTexto}%, linha ${resultado.ancora.numeroLinha} da planilha)`
                                            : ""}
                                    </Typography>

                                    {resultado.erros.map((erro) => (
                                        <Alert severity="error" sx={{ mb: 1 }} key={erro}>
                                            {erro}
                                        </Alert>
                                    ))}

                                    {resultado.avisos.map((aviso) => (
                                        <Alert severity="warning" sx={{ mb: 1 }} key={aviso}>
                                            {aviso}
                                        </Alert>
                                    ))}

                                    {previa.length > 0 && (
                                        <Box sx={{ overflowX: "auto", mb: 1 }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>UC</TableCell>
                                                        <TableCell>CPF/CNPJ</TableCell>
                                                        <TableCell>Cota</TableCell>
                                                        <TableCell>Âncora</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {previa.map((linha) => (
                                                        <TableRow
                                                            key={linha.numeroLinha}
                                                            sx={{
                                                                backgroundColor: linha.ancora
                                                                    ? "action.selected"
                                                                    : "transparent",
                                                            }}
                                                        >
                                                            <TableCell>{linha.uc}</TableCell>
                                                            <TableCell>{linha.documento}</TableCell>
                                                            <TableCell>{linha.cotaTexto}</TableCell>
                                                            <TableCell>{linha.ancora ? "Sim" : ""}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>

                                            {resultado.linhas.length > previa.length && (
                                                <Typography variant="caption">
                                                    Prévia das {previa.length} primeiras linhas de{" "}
                                                    {resultado.linhas.length}.
                                                </Typography>
                                            )}
                                        </Box>
                                    )}

                                    <Divider sx={{ mb: 1 }} />

                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<FileDownloadOutlinedIcon />}
                                            disabled={bloqueado}
                                            onClick={() => baixar(resultado)}
                                        >
                                            Baixar CSV
                                        </Button>

                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<ContentCopyIcon />}
                                            disabled={bloqueado}
                                            onClick={() => copiar(resultado)}
                                        >
                                            Copiar CSV
                                        </Button>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}

                    <Grid item xs={12}>
                        <Typography variant="caption">
                            O arquivo é processado inteiramente no seu navegador. Nada é enviado para servidor.
                        </Typography>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions style={{ display: "flex", justifyContent: "space-between", margin: "16px 24px" }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={incluirCabecalho}
                            onChange={(evento) => setIncluirCabecalho(evento.target.checked)}
                        />
                    }
                    label="Incluir linha de cabeçalho"
                />

                <Box sx={{ display: "flex", gap: 1 }}>
                    {totalValidos > 1 && (
                        <Button
                            onClick={baixarTodos}
                            color="primary"
                            variant="contained"
                            startIcon={<FileDownloadOutlinedIcon />}
                        >
                            Baixar todos ({totalValidos})
                        </Button>
                    )}

                    <Button onClick={onClose} color="warning" variant="contained" style={{ width: "159px" }}>
                        Fechar
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}
